import client from './client';

const ADMIN_IMAGE_UPLOAD_TARGET_BYTES = 850 * 1024;
const ADMIN_IMAGE_UPLOAD_MAX_DIMENSION = 1800;
const ADMIN_IMAGE_UPLOAD_QUALITY_STEPS = [0.84, 0.76, 0.68, 0.6, 0.52, 0.44, 0.36];

const getUploadFilename = (name, mimeType) => {
  const base = String(name || 'admin-image').replace(/\.[^.]+$/, '') || 'admin-image';
  const ext = mimeType === 'image/jpeg' ? 'jpg' : mimeType === 'image/png' ? 'png' : 'webp';
  return `${base}.${ext}`;
};

const loadImageForCompression = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(url);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('تصویر قابل پردازش نیست. لطفاً یک فایل تصویری معتبر انتخاب کنید.'));
  };
  image.src = url;
});

const renderImageToCanvas = (image, maxDimension, fillWhite = false) => {
  const ratio = Math.min(1, maxDimension / image.width, maxDimension / image.height);
  const width = Math.max(1, Math.round(image.width * ratio));
  const height = Math.max(1, Math.round(image.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (fillWhite) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(image, 0, 0, width, height);
  return canvas;
};

const canvasToBlob = (canvas, mimeType, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => {
    resolve(blob && blob.type === mimeType ? blob : null);
  }, mimeType, quality);
});

async function prepareAdminImageUpload(file) {
  if (!file || !file.type?.startsWith('image/') || file.size <= ADMIN_IMAGE_UPLOAD_TARGET_BYTES) {
    return { blob: file, filename: file?.name || 'admin-image' };
  }

  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof Image === 'undefined') {
    return { blob: file, filename: file.name || 'admin-image' };
  }

  const image = await loadImageForCompression(file);
  let maxDimension = ADMIN_IMAGE_UPLOAD_MAX_DIMENSION;
  let bestBlob = null;

  for (let attempt = 0; attempt < 6; attempt += 1) {
    const webpCanvas = renderImageToCanvas(image, maxDimension);
    for (const quality of ADMIN_IMAGE_UPLOAD_QUALITY_STEPS) {
      const blob = await canvasToBlob(webpCanvas, 'image/webp', quality);
      if (blob && (!bestBlob || blob.size < bestBlob.size)) bestBlob = blob;
      if (blob && blob.size <= ADMIN_IMAGE_UPLOAD_TARGET_BYTES) {
        return { blob, filename: getUploadFilename(file.name, blob.type) };
      }
    }

    const jpegCanvas = renderImageToCanvas(image, maxDimension, true);
    for (const quality of ADMIN_IMAGE_UPLOAD_QUALITY_STEPS) {
      const blob = await canvasToBlob(jpegCanvas, 'image/jpeg', quality);
      if (blob && (!bestBlob || blob.size < bestBlob.size)) bestBlob = blob;
      if (blob && blob.size <= ADMIN_IMAGE_UPLOAD_TARGET_BYTES) {
        return { blob, filename: getUploadFilename(file.name, blob.type) };
      }
    }

    maxDimension = Math.max(420, Math.round(maxDimension * 0.78));
  }

  if (bestBlob && bestBlob.size <= 1024 * 1024) {
    return { blob: bestBlob, filename: getUploadFilename(file.name, bestBlob.type) };
  }

  throw new Error('حجم تصویر حتی بعد از فشرده‌سازی زیاد است. لطفاً تصویر کوچک‌تری انتخاب کنید.');
}

export const adminAPI = {
  // Invitations
  createInvitation: (data) =>
    client.post('/admin/invitations/create', data),

  getInvitations: (page = 1, limit = 10, used = null) => {
    const params = { page, limit };
    if (used !== null) params.used = used;
    return client.get('/admin/invitations', { params });
  },

  updateInvitation: (id, data) => client.put(`/admin/invitations/${id}`, data),
  toggleInvitation: (id) => client.patch(`/admin/invitations/${id}/toggle`),
  resendInvitationSms: (id) => client.post(`/admin/invitations/${id}/resend-sms`),
  deleteInvitation: (id) => client.delete(`/admin/invitations/${id}`),

  // Waitlist
  getWaitlist: (params = {}) => client.get('/admin/waitlist', { params }),
  updateWaitlistEntry: (id, data) => client.put(`/admin/waitlist/${id}`, data),
  inviteWaitlistEntry: (id) => client.post(`/admin/waitlist/${id}/invite`),
  resendWaitlistSms: (id) => client.post(`/admin/waitlist/${id}/resend-sms`),

  // Requirements
  createRequirement: (formData) =>
    client.post('/admin/requirements', formData),

  getRequirements: (year = 1) =>
    client.get('/admin/requirements', { params: { year } }),

  updateRequirement: (id, formData) =>
    client.put(`/admin/requirements/${id}`, formData),

  deleteRequirement: (id) =>
    client.delete(`/admin/requirements/${id}`),

  // SMS Templates
  getSMSTemplates: () =>
    client.get('/admin/sms-templates'),

  createSMSTemplate: (data) =>
    client.post('/admin/sms-templates', data),

  updateSMSTemplate: (id, data) =>
    client.put(`/admin/sms-templates/${id}`, data),

  // Users
  getAllUsers: (page = 1, limit = 10, filters = {}) =>
    client.get('/admin/users', { params: { page, limit, ...filters } }),

  banUser: (id, reason) => client.patch(`/admin/users/${id}/ban`, { reason }),
  unbanUser: (id) => client.patch(`/admin/users/${id}/unban`),
  sendCustomMessageToUser: (userId, data) => client.post(`/admin/users/${userId}/send-message`, data),

  // Devices & Blacklist
  getDevices: (page = 1, limit = 10, filters = {}) =>
    client.get('/admin/devices', { params: { page, limit, ...filters } }),
  getDeviceStats: () =>
    client.get('/admin/devices/stats'),
  banDevice: (deviceId, reason) =>
    client.post('/admin/devices/ban', { deviceId, reason }),
  unbanDevice: (deviceId) =>
    client.post('/admin/devices/unban', { deviceId }),
  getBlacklist: (page = 1, limit = 10, type = null) => {
    const params = { page, limit };
    if (type) params.type = type;
    return client.get('/admin/blacklist', { params });
  },
  banPhoneNumber: (phoneNumber, reason) =>
    client.post('/admin/blacklist/phone/ban', { phoneNumber, reason }),
  unbanPhoneNumber: (phoneNumber) =>
    client.post('/admin/blacklist/phone/unban', { phoneNumber }),

  // Dashboard
  getDashboardStats: () =>
    client.get('/admin/dashboard/stats'),

  // SMS Settings
  getSMSSettings: () =>
    client.get('/admin/sms-settings'),

  updateSMSSettings: (data) =>
    client.put('/admin/sms-settings', data),

  testSMSConfig: (phone) =>
    client.post('/admin/sms-settings/test', { phone }),

  // Email Settings (SMTP)
  getEmailSettings: () =>
    client.get('/admin/email-settings'),

  updateEmailSettings: (data) =>
    client.put('/admin/email-settings', data),

  testEmailConfig: (email) =>
    client.post('/admin/email-settings/test', { email }),

  // Payment Settings (زرین‌پال)
  getPaymentSettings: () =>
    client.get('/admin/payment-settings'),

  updatePaymentSettings: (data) =>
    client.put('/admin/payment-settings', data),

  // Telegram Bot Settings (تسک عضویت خودکار کانال)
  getTelegramBotSettings: () =>
    client.get('/admin/telegram-settings'),

  updateTelegramBotSettings: (data) =>
    client.put('/admin/telegram-settings', data),

  // Languages Settings
  getLanguagesSettings: () =>
    client.get('/admin/languages-settings'),

  updateLanguagesSettings: (data) =>
    client.put('/admin/languages-settings', data),


  // تغییر رمز عبور خودِ ادمین/استاد
  changeMyPassword: (currentPassword, newPassword) =>
    client.put('/admin/change-password', { currentPassword, newPassword }),

  // Transactions (تراکنش‌های همه کاربران — پرداخت‌های واقعی + عملیات بانک گرینگوتز)
  getAllTransactions: (page = 1, limit = 10, filters = {}) =>
    client.get('/payment/admin/transactions', {
      params: { page, limit, ...filters }
    }),

  // ─── بانک گرینگوتز (فقط مدیر) ────────────────────────────────────────────
  // نرخ ارز: poundPerGalleon ثابت است؛ tomanPerPound (پوند به تومان) و
  // tomanPerUsd (دلار به تومان) از پنل ادمین تنظیم می‌شوند و usdPerPound
  // (لازم برای پرداخت تتری/OxaPay) سمت سرور از این دو نرخ محاسبه می‌شود.
  getExchangeRate: () => client.get('/admin/exchange-rate'),
  updateExchangeRate: (tomanPerPound, tomanPerUsd) => client.put('/admin/exchange-rate', { tomanPerPound, tomanPerUsd }),

  // به‌روزرسانی خودکار نرخ ارز از نوسان (navasan.tech) — دو بار در روز + گزارش تاریخچه
  getNavasanSettings: () => client.get('/admin/navasan-settings'),
  updateNavasanSettings: (data) => client.put('/admin/navasan-settings', data),
  fetchExchangeRateNow: () => client.post('/admin/exchange-rate/fetch-now'),
  getExchangeRateHistory: (page = 1, limit = 20) =>
    client.get('/admin/exchange-rate/history', { params: { page, limit } }),

  // آمار کلی بانک: تعداد خزانه‌ها، تراکنش‌ها و مجموع پول در گردش
  getBankStats: () => client.get('/admin/bank/stats'),

  // فهرست خزانه‌های همه کاربران
  getBankVaults: (page = 1, limit = 20, filters = {}) =>
    client.get('/admin/bank/vaults', { params: { page, limit, ...filters } }),
  getBankVault: (userId) => client.get(`/admin/bank/vaults/${userId}`),
  lockBankVault: (userId, reason) => client.patch(`/admin/bank/vaults/${userId}/lock`, { reason }),
  unlockBankVault: (userId) => client.patch(`/admin/bank/vaults/${userId}/unlock`),
  adjustBankBalance: (userId, { knuts, target, description }) =>
    client.post(`/admin/bank/vaults/${userId}/adjust`, { knuts, target, description }),

  // Year Game
  getYearGameYears: () => client.get('/admin/year-game/years'),
  createYearGameYears: (data) => client.post('/admin/year-game/years', data),
  updateYearGameYears: (id, data) => client.put(`/admin/year-game/years/${id}`, data),
  deleteYearGameYears: (id) => client.delete(`/admin/year-game/years/${id}`),

  getYearGameMissions: () => client.get('/admin/year-game/missions'),
  createYearGameMissions: (data) => client.post('/admin/year-game/missions', data),
  updateYearGameMissions: (id, data) => client.put(`/admin/year-game/missions/${id}`, data),
  deleteYearGameMissions: (id) => client.delete(`/admin/year-game/missions/${id}`),

  getYearGameSkills: () => client.get('/admin/year-game/skills'),
  createYearGameSkills: (data) => client.post('/admin/year-game/skills', data),
  updateYearGameSkills: (id, data) => client.put(`/admin/year-game/skills/${id}`, data),
  deleteYearGameSkills: (id) => client.delete(`/admin/year-game/skills/${id}`),

  getYearGameTasks: () => client.get('/admin/year-game/tasks'),
  createYearGameTasks: (data) => client.post('/admin/year-game/tasks', data),
  updateYearGameTasks: (id, data) => client.put(`/admin/year-game/tasks/${id}`, data),
  deleteYearGameTasks: (id) => client.delete(`/admin/year-game/tasks/${id}`),

  getYearGameMiniGames: () => client.get('/admin/year-game/mini-games'),
  createYearGameMiniGames: (data) => client.post('/admin/year-game/mini-games', data),
  updateYearGameMiniGames: (id, data) => client.put(`/admin/year-game/mini-games/${id}`, data),
  deleteYearGameMiniGames: (id) => client.delete(`/admin/year-game/mini-games/${id}`),

  getYearGameTaskSubmissions: (status) => client.get('/admin/year-game/task-submissions', { params: { status } }),
  reviewYearGameTaskSubmission: (id, status) => client.patch(`/admin/year-game/task-submissions/${id}/review`, { status }),
  getYearGameMiniGameRegistrations: (status) => client.get('/admin/year-game/mini-game-registrations', { params: { status } }),
  reviewYearGameMiniGameRegistration: (id, status) => client.patch(`/admin/year-game/mini-game-registrations/${id}/review`, { status }),

  // Registration settings (کنترل ثبت‌نام و الزام کد دعوت)
  getRegistrationSettings: () => client.get('/admin/registration-settings'),
  updateRegistrationSettings: (data) => client.put('/admin/registration-settings', data),

  // API Key Manager (سویچر کلیدهای AI)
  getApiKeys: () => client.get('/admin/api-keys'),
  addApiKey: (providerName, apiKey) => client.post(`/admin/api-keys/${providerName}`, { apiKey }),
  removeApiKey: (providerName, keyIndex) => client.delete(`/admin/api-keys/${providerName}/${keyIndex}`),

  // Game steps (مراحل بازی)
  getGameSteps: () => client.get('/admin/game-steps'),
  createGameStep: (data) => client.post('/admin/game-steps', data),
  updateGameStep: (id, data) => client.put(`/admin/game-steps/${id}`, data),
  toggleGameStep: (id) => client.patch(`/admin/game-steps/${id}/toggle`),
  reorderGameSteps: (order) => client.put('/admin/game-steps/reorder', { order }),
  deleteGameStep: (id) => client.delete(`/admin/game-steps/${id}`),

  // FAQ (سوالات متداول)
  getFAQs: () => client.get('/admin/faq'),
  createFAQ: (data) => client.post('/admin/faq', data),
  updateFAQ: (id, data) => client.put(`/admin/faq/${id}`, data),
  toggleFAQ: (id) => client.patch(`/admin/faq/${id}/toggle`),
  reorderFAQs: (order) => client.put('/admin/faq/reorder', { order }),
  deleteFAQ: (id) => client.delete(`/admin/faq/${id}`),

  // AI Chat
  getChatSettings: () => client.get('/admin/chat/settings'),
  updateChatSettings: (data) => client.put('/admin/chat/settings', data),
  getChatStats: () => client.get('/admin/chat/stats'),
  getChatConversations: (page = 1, limit = 30) =>
    client.get('/admin/chat/conversations', { params: { page, limit } }),
  getChatErrors: (limit = 50) =>
    client.get('/admin/chat/errors', { params: { limit } }),

  // Train monitoring, chat & bans
  getTrainOverview: () => client.get('/admin/train/overview'),
  getTrainChat: () => client.get('/admin/train/chat'),
  postTrainChat: (content) => client.post('/admin/train/chat', { content }),
  getTrainBanned: () => client.get('/admin/train/banned'),
  unbanTrainUser: (id) => client.post(`/admin/train/users/${id}/unban`),

  // Generic App Config (تنظیمات عمومی/احتمالات/Tagها/پیام‌های سایت)
  getAppConfig: (key) => client.get(`/admin/app-config/${key}`),
  upsertAppConfig: (key, value) => client.put(`/admin/app-config/${key}`, { value }),
  resetAppConfig: (key) => client.post(`/admin/app-config/${key}/reset`),

  // آپلود عمومی تصویر (مثلاً برای محتوای صفحه اصلی) — مسیر فایل را برمی‌گرداند
  uploadImage: async (file) => {
    const prepared = await prepareAdminImageUpload(file);
    const formData = new FormData();
    formData.append('image', prepared.blob, prepared.filename);
    // Content-Type را دستی ست نمی‌کنیم — مرورگر boundary را خودکار اضافه می‌کند.
    // ست‌کردن دستی 'multipart/form-data' بدون boundary در axios v1.x باعث می‌شد
    // multer نتواند فایل را parse کند و req.file خالی می‌ماند.
    try {
      return await client.post('/admin/upload-image', formData);
    } catch (err) {
      if (err.response?.status === 413) {
        err.message = 'حجم تصویر از سقف مجاز سرور بیشتر است. لطفاً تصویر کوچک‌تری انتخاب کنید.';
      }
      throw err;
    }
  },

  // محتوای صفحه اصلی (Landing Page) — fa/en، خوانده و ذخیره‌شده روی کلید landing_page
  getLandingContent: () => client.get('/admin/app-config/landing_page'),
  upsertLandingContent: (value) => client.put('/admin/app-config/landing_page', { value }),

  // محتوای صفحه «مراحل بازی و داستان هاگوارتز» (About Page) — fa/en، خوانده و ذخیره‌شده روی کلید about_page
  getAboutContent: () => client.get('/admin/app-config/about_page'),
  upsertAboutContent: (value) => client.put('/admin/app-config/about_page', { value }),

  // لینک‌های منوی هدر و فوتر — خوانده و ذخیره‌شده روی کلید site_nav
  getSiteNav: () => client.get('/admin/app-config/site_nav'),
  upsertSiteNav: (value) => client.put('/admin/app-config/site_nav', { value }),

  // افزایش سقف تلاش کاربر برای عبور از سکو
  setPlatformAttempts: (userId, maxAttemptsOverride) =>
    client.patch(`/admin/users/${userId}/platform-attempts`, { maxAttemptsOverride }),

  // دسترسی آزاد و رایگان به قطار (فقط مدیر) — مثلاً برای اساتید
  setFreeTrainAccess: (userId, enabled) =>
    client.patch(`/admin/users/${userId}/free-train-access`, { enabled }),

  // Person / Journey / Tag / Timeline
  getPersons: (params = {}) => client.get('/admin/persons', { params }),
  getPerson: (id) => client.get(`/admin/persons/${id}`),
  addPersonTag: (id, tag) => client.post(`/admin/persons/${id}/tags`, { tag }),
  removePersonTag: (id, tag) => client.delete(`/admin/persons/${id}/tags/${tag}`),
  setPersonJourneyStage: (id, stage) => client.patch(`/admin/persons/${id}/journey`, { stage }),

  // Train schedules (زمان‌های حرکت هاگوارتز اکسپرس)
  getTrainSchedules: () => client.get('/admin/train/schedules'),
  createTrainSchedule: (data) => client.post('/admin/train/schedules', data),
  updateTrainSchedule: (id, data) => client.put(`/admin/train/schedules/${id}`, data),
  deleteTrainSchedule: (id) => client.delete(`/admin/train/schedules/${id}`),
  assignTrainScheduleToInvitation: (invitationId, scheduleId) =>
    client.patch(`/admin/train/schedules/assign/${invitationId}`, { scheduleId }),

  // Admin Notifications
  getAdminNotifications: () => client.get('/admin/notifications'),
  markNotificationRead: (id) => client.patch(`/admin/notifications/${id}/read`),

  // Security Logs (فعالیت‌های مشکوک)
  getSecurityLogs: (page = 1, limit = 30, filters = {}) =>
    client.get('/admin/security-logs', { params: { page, limit, ...filters } }),
  markSecurityLogRead: (id) => client.patch(`/admin/security-logs/${id}/read`),
  markAllSecurityLogsRead: () => client.patch('/admin/security-logs/read-all'),

  // Database backup (بکاپ کامل دیتابیس) — خروجی فایل باینری
  downloadDatabaseBackup: (format = null) =>
    client.get('/admin/backup/database', {
      params: format ? { format } : {},
      responseType: 'blob',
      timeout: 0 // بکاپ ممکن است طول بکشد؛ بدون محدودیت زمانی
    }),

  // آمار لحظه‌ای دیتابیس (تعداد کاربر، استاد، لیست انتظار و ...)
  getBackupStats: () => client.get('/admin/backup/stats'),

  // بکاپ‌های ذخیره‌شده روی سرور
  createServerBackup: (format = null) =>
    client.post('/admin/backup/server', {}, {
      params: format ? { format } : {},
      timeout: 0
    }),
  listServerBackups: () => client.get('/admin/backup/server'),
  downloadServerBackup: (filename) =>
    client.get(`/admin/backup/server/${encodeURIComponent(filename)}/download`, {
      responseType: 'blob',
      timeout: 0
    }),
  deleteServerBackup: (filename) => client.delete(`/admin/backup/server/${encodeURIComponent(filename)}`),

  // بازیابی (Restore) — نیازمند عبارت تاییدی «RESTORE»
  restoreFromUpload: (file, confirmPhrase, format = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('confirmPhrase', confirmPhrase);
    if (format) formData.append('format', format);
    return client.post('/admin/backup/restore/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0
    });
  },
  restoreFromServerBackup: (filename, confirmPhrase) =>
    client.post(`/admin/backup/server/${encodeURIComponent(filename)}/restore`, { confirmPhrase }, { timeout: 0 }),

  // تنظیمات بکاپ خودکار شبانه — از طریق App Config عمومی (کلید backup_settings)
  getBackupSettings: () => client.get('/admin/app-config/backup_settings'),
  updateBackupSettings: (value) => client.put('/admin/app-config/backup_settings', { value }),

  // ─── مدیریت حساب‌های ادمین/استاد ─────────────────────────────────────────
  getAdmins: (role = null) => client.get('/admin/admins', { params: role ? { role } : {} }),
  createAdminAccount: (data) => client.post('/admin/admins', data),
  updateAdminAccount: (id, data) => client.put(`/admin/admins/${id}`, data),
  resetAdminPassword: (id, newPassword) => client.patch(`/admin/admins/${id}/reset-password`, { newPassword }),
  deleteAdminAccount: (id) => client.delete(`/admin/admins/${id}`),
  promoteUserToTeacher: (userId, data) => client.post(`/admin/users/${userId}/promote-to-teacher`, data),

  // Contact Messages
  getContactMessages: (page = 1, limit = 20) =>
    client.get('/admin/contact-messages', { params: { page, limit } }),
  markContactMessageRead: (id) =>
    client.patch(`/admin/contact-messages/${id}/read`),
  deleteContactMessage: (id) =>
    client.delete(`/admin/contact-messages/${id}`),

  // ─── مانیتورینگ خطاها — بک‌اند اصلی + چت هوش مصنوعی + کوچه دیاگون یکجا ──────
  getErrors: (params = {}) => client.get('/admin/errors', { params }),
  getErrorStats: () => client.get('/admin/errors/stats'),
  resolveError: (id, source) =>
    client.put(`/admin/errors/${id}/resolve`, {}, { params: { source } }),
  deleteError: (id, source) =>
    client.delete(`/admin/errors/${id}`, { params: { source } }),

  // ─── مانیتورینگ درخواست‌های سرور ───────────────────────────────────────────
  getRequestLogs: (params = {}) => client.get('/admin/request-logs', { params }),
  clearRequestLogs: () => client.delete('/admin/request-logs'),

  // ─── Analytics Tracking (GTM/GA) ──────────────────────────────────────────
  getAnalyticsTrackingConfig: () => client.get('/admin/analytics-tracking'),
  updateAnalyticsTrackingConfig: (data) => client.put('/admin/analytics-tracking', data),

  // ─── نتایج آزمون‌ها (Sorting Hat/Wand/Patronus/Platform9¾) ─────────────────
  getTestResults: (params = {}) => client.get('/admin/test-results', { params }),

  // ─── چت هوش مصنوعی — جزئیات یک گفتگو برای Drawer ───────────────────────────
  getChatConversation: (id) => client.get(`/admin/chat/conversations/${id}`),

  // ─── ماموریت‌های بازی سالانه (مجزا از ماموریت‌های کوچه دیاگون) ────────────
  getGameMissions: (params = {}) => client.get('/admin/game-missions', { params }),
  createGameMission: (data) => client.post('/admin/game-missions', data),
  updateGameMission: (id, data) => client.put(`/admin/game-missions/${id}`, data),
  deleteGameMission: (id) => client.delete(`/admin/game-missions/${id}`),
  getGameUserMissions: (params = {}) => client.get('/admin/game-user-missions', { params }),

  // ─── مدیریت فروم ────────────────────────────────────────────────────────────
  getForumPosts: (params = {}) => client.get('/admin/forum-posts', { params }),
  deleteForumPost: (id) => client.delete(`/admin/forum-posts/${id}`),
  togglePinForumPost: (id) => client.patch(`/admin/forum-posts/${id}/pin`),

  // ─── لاگ پیامک‌ها ───────────────────────────────────────────────────────────
  getSMSLogs: (params = {}) => client.get('/admin/sms-logs', { params }),

  // ─── کوچه دیاگون — پروکسی سمت سرور ──────────────────────────────────────────
  getDiagonAlleyVaults: (params = {}) => client.get('/admin/diagon-alley/vaults', { params }),
  getDiagonAlleyOrders: (params = {}) => client.get('/admin/diagon-alley/orders', { params }),

  getDiagonAlleyDiscounts: () => client.get('/admin/diagon-alley/discounts'),
  createDiagonAlleyDiscount: (data) => client.post('/admin/diagon-alley/discounts', data),
  updateDiagonAlleyDiscount: (id, data) => client.put(`/admin/diagon-alley/discounts/${id}`, data),
  deleteDiagonAlleyDiscount: (id) => client.delete(`/admin/diagon-alley/discounts/${id}`),

  getDiagonAlleyAchievements: () => client.get('/admin/diagon-alley/achievements'),
  createDiagonAlleyAchievement: (data) => client.post('/admin/diagon-alley/achievements', data),
  updateDiagonAlleyAchievement: (id, data) => client.put(`/admin/diagon-alley/achievements/${id}`, data),

  getDiagonAlleyMissions: () => client.get('/admin/diagon-alley/missions'),
  createDiagonAlleyMission: (data) => client.post('/admin/diagon-alley/missions', data),
  updateDiagonAlleyMission: (id, data) => client.put(`/admin/diagon-alley/missions/${id}`, data),

  getDiagonAlleyConfig: () => client.get('/admin/diagon-alley/config'),
  setDiagonAlleyConfig: (key, value) => client.post('/admin/diagon-alley/config', { key, value }),

  getDiagonAlleyUsers: (params = {}) => client.get('/admin/diagon-alley/users', { params }),
};
